import React, { useCallback, useState } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import { composeUsername } from '../../../../../lib/DisplayName';
import { roomOwnerUserFields } from '../../../../../graphql/User/UserFields';
import { USERS_PAGINATION_MAX_AMOUNT } from '../../../../../enums/Constraints';
import { UserSchema } from '../../../../../definitions/schemas';
import useResource from '../../../../../lib/hook/useResource';
import UserInterface from '../../../../../graphql/User/UserInterface';
import UserStatus from '../../../../../enums/UserStatus';

export interface RoomOwnerProps {
  onRoomOwnerChange?: (value: UserSchema) => void;
  owner: UserSchema;
}

const RoomOwner: React.FC<RoomOwnerProps> = (
  props: RoomOwnerProps
): JSX.Element => {
  const { onRoomOwnerChange = () => {}, owner } = props;

  const [value, setValue] = useState(owner);

  const [users, usersReady] = useResource<UserSchema[]>(
    () => loadUsers(),
    [],
    []
  );

  const loadUsers = async (): Promise<UserSchema[]> => {
    const res = await new UserInterface().search(
      { first: USERS_PAGINATION_MAX_AMOUNT * 2, status: UserStatus.ACTIVE },
      roomOwnerUserFields
    );

    return res.nodes;
  };

  const onChangeHandler = useCallback(
    (newOwner: UserSchema) => {
      setValue(newOwner);
      onRoomOwnerChange(newOwner);
    },
    [onRoomOwnerChange]
  );

  return (
    <Autocomplete
      id="room-owner"
      color="secondary"
      disableClearable
      getOptionLabel={(option: UserSchema) => composeUsername(option)}
      isOptionEqualToValue={(option: UserSchema, optionValue: UserSchema) =>
        option.id === optionValue.id
      }
      onChange={(_, newValue: UserSchema) => {
        onChangeHandler(newValue);
      }}
      data-testid="dropdown-room-owner"
      options={users}
      renderInput={(params) => (
        <TextField
          {...params}
          color="secondary"
          fullWidth
          label="Room Owner"
          variant="outlined"
        />
      )}
      loading={!usersReady}
      value={value}
    />
  );
};

export default RoomOwner;
